/**
 * Betting Tips
 * Shared content and logic for contextual betting tips shown to new users.
 * Tips show once by default, auto-disable after all are seen, and can be
 * re-enabled from Settings.
 */

// =============================================================================
// Types
// =============================================================================

export interface BettingTip {
  id: string;
  title: string;
  body: string;
  linkText: string;
}

// =============================================================================
// Tip Content
// =============================================================================

export const BETTING_TIPS = {
  newUserHero: {
    id: 'new_user_hero',
    title: 'New here? Explore before you bet',
    body: 'Run a few generations to see how picks settle across risk tiers and prop types. Treat your first few days as a research phase — no real money needed.',
    linkText: 'Read the Smart Betting Guide',
  },
  firstGeneration: {
    id: 'first_generation',
    title: 'Watch before you wager',
    body: 'These are AI-generated recommendations, not certainties. Track a few days of picks before betting real money to learn what works for your style and settings.',
    linkText: 'Read the Smart Betting Guide',
  },
  firstTrack: {
    id: 'first_track',
    title: 'Track a few days first',
    body: "Nice — you're tracking picks! Run a few more days of generations before placing real bets. Watch how different risk tiers and prop types settle to find your edge.",
    linkText: 'Read the Smart Betting Guide',
  },
} as const satisfies Record<string, BettingTip>;

export type BettingTipId = (typeof BETTING_TIPS)[keyof typeof BETTING_TIPS]['id'];

export const ALL_TIP_IDS: BettingTipId[] = Object.values(BETTING_TIPS).map((t) => t.id);

// =============================================================================
// Visibility Logic
// =============================================================================

/**
 * Check if a specific tip should be shown.
 * Returns false if the master toggle is off or the tip has been seen.
 */
export function shouldShowTip(
  tipId: string,
  showBettingTips: boolean | undefined,
  seenTipIds: string[],
): boolean {
  if (showBettingTips === false) return false;
  if (seenTipIds.includes(tipId)) return false;
  return true;
}

/**
 * Check if all tips have been seen (triggers auto-disable of master toggle).
 */
export function allTipsSeen(seenTipIds: string[]): boolean {
  return ALL_TIP_IDS.every((id) => seenTipIds.includes(id));
}
